import { isUndefined } from 'lodash';
import pluralize from 'pluralize';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useUnmount } from 'react-use';

import { type Clip, type Discussion } from '@air/api/types';
import { ChevronDownIcon, ChevronRightIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { getPageNumber, hasAnnotation } from '~/components/Annotations/shared/annotationUtils';
import { type ClipDiscussion as ClipDiscussionType } from '~/components/Annotations/shared/types';
import {
  useDiscussionPanelContext,
  useDiscussionPanelContextSelector,
} from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { type DiscussionsPanelContextValue } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextTypes';
import { isDiscussionRepliesOpenSelector } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextUtils';
import { CommentReply } from '~/components/DiscussionsV2/Discussion/components/CommentReply';
import { ReplyButton } from '~/components/DiscussionsV2/Discussion/components/ReplyButton';
import { TopCommentContainer } from '~/components/DiscussionsV2/Discussion/components/TopCommentContainer';
import { CommentTimestampInfo } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/CommentTimestampInfo';
import { DiscussionAnnotationInfo } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/DiscussionAnnotationInfo';
import {
  DiscussionComment,
  type DiscussionCommentProps,
} from '~/components/DiscussionsV2/Discussion/DiscussionComment/DiscussionComment';
import { COMMENT_AUTHOR_HEADER_LEFT } from '~/components/DiscussionsV2/Discussion/shared/constants';
import { type MentionsData } from '~/components/DiscussionsV2/DiscussionForm';
import { DISCUSSION } from '~/constants/testIDs';
import { useAccountContext } from '~/providers/AccountProvider';
import { centralizedClipVersionNumberSelector } from '~/store/centralizedClip/selectors';
import { isViewingDiscussionWithId } from '~/store/router/selectors';
import { useAirSelector } from '~/utils/ReduxUtils';

export type ClipDiscussionProps = {
  discussion: ClipDiscussionType;
  canReply?: boolean;
  /**
   * @see https://github.com/signavio/react-mentions
   */
  mentionsList: MentionsData[];
  isLoadingMentions?: boolean;

  onToggleResolve: DiscussionCommentProps['onToggleResolve'];
  onDelete: DiscussionCommentProps['onDelete'];
  onEdit: DiscussionCommentProps['onSave'];
  onAnnotatedCommentClick: (props: {
    clipId: Clip['id'];
    discussionId: Discussion['id'];
    timestamp?: number;
    hasAnnotation: boolean;
    pageNumber?: number;
  }) => void;

  onSubmit?: (comment: string, discussion: ClipDiscussionType, clipId: Clip['id']) => void;

  onSwitchVersion?: DiscussionCommentProps['onSwitchVersion'];
  shouldShowMentionsButton?: boolean;
  clipId: Clip['id'];
};

export const ClipDiscussion = memo(
  ({
    discussion,
    isLoadingMentions,
    mentionsList,
    canReply,
    onEdit,
    onDelete,
    onToggleResolve,
    onSubmit,
    onSwitchVersion,
    shouldShowMentionsButton,
    clipId,
    onAnnotatedCommentClick,
  }: ClipDiscussionProps) => {
    const { data } = useAccountContext();
    const currentAssetVersion = useSelector(centralizedClipVersionNumberSelector);
    const isDiscussionActive = useAirSelector((st) => isViewingDiscussionWithId(st, discussion.id));
    const { clearReplyingDiscussionId, setRepliesOpen } = useDiscussionPanelContext();

    const isReplyingCurrentDiscussionIdSelector = useCallback(
      (state: DiscussionsPanelContextValue) => state.replyingDiscussionId === discussion.id,
      [discussion.id],
    );

    const repliesVisibleSelector = useCallback(
      (state: DiscussionsPanelContextValue) => {
        return isDiscussionRepliesOpenSelector(state, discussion.id);
      },
      [discussion.id],
    );

    const isAddingReply = useDiscussionPanelContextSelector(isReplyingCurrentDiscussionIdSelector);
    const areRepliesVisible = useDiscussionPanelContextSelector(repliesVisibleSelector);

    useEffect(() => {
      if (isAddingReply) {
        setRepliesOpen(discussion.id, true);
      }
    }, [discussion.id, isAddingReply, setRepliesOpen]);

    const commentProps = useMemo(
      (): Omit<DiscussionCommentProps, 'comment'> => ({
        currentVersion: currentAssetVersion,
        discussion,
        isLoadingMentions,
        mentionsList,
        onDelete,
        onSave: onEdit,
        shouldShowMentionsButton,
        onToggleResolve,
        onSwitchVersion: onSwitchVersion ? () => onSwitchVersion(discussion.clipId) : undefined,
        showAvatar: true,
      }),
      [
        currentAssetVersion,
        discussion,
        isLoadingMentions,
        mentionsList,
        onDelete,
        onEdit,
        onSwitchVersion,
        onToggleResolve,
        shouldShowMentionsButton,
      ],
    );

    const canReplyToComment = canReply && !discussion.resolved;

    const onReplySubmit = useCallback(
      (newComment: string) => {
        onSubmit?.(newComment, discussion, clipId);
        setRepliesOpen(discussion.id, true);
      },
      [clipId, discussion, onSubmit, setRepliesOpen],
    );

    const comments = discussion.comments.data;

    const toggleReplies = useCallback(() => {
      setRepliesOpen(discussion.id, !areRepliesVisible);
    }, [areRepliesVisible, discussion.id, setRepliesOpen]);

    const handleCommentClick = useCallback(() => {
      // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
      onAnnotatedCommentClick?.({
        clipId: discussion.clipId,
        discussionId: discussion.id,
        timestamp: discussion.timestamp,
        hasAnnotation: hasAnnotation(discussion),
        pageNumber: getPageNumber(discussion.annotation),
      });
    }, [discussion, onAnnotatedCommentClick]);

    useUnmount(() => {
      if (isAddingReply) {
        clearReplyingDiscussionId();
      }
    });

    const timestamp = discussion.timestamp ?? discussion.annotation?.timestamp;
    const endTimestamp = discussion.endTimestamp ?? discussion.annotation?.endTimestamp;

    const commentMetadataInfo = useMemo(() => {
      const color = isDiscussionActive ? 'teal' : 'grey';

      if (hasAnnotation(discussion)) {
        return <DiscussionAnnotationInfo discussion={discussion} color={color} endTimestamp={endTimestamp} />;
      } else if (!isUndefined(timestamp)) {
        return <CommentTimestampInfo timestamp={timestamp} endTimestamp={endTimestamp} color={color} />;
      }

      return null;
    }, [discussion, endTimestamp, isDiscussionActive, timestamp]);

    return (
      <div data-discussionid={discussion.id} data-testid={DISCUSSION}>
        <TopCommentContainer discussion={discussion} onClick={handleCommentClick}>
          <DiscussionComment
            className={areRepliesVisible ? 'ads:bg-raised bg-gray-2' : ''}
            commentMetadata={commentMetadataInfo}
            commentMetadataOnOwnLine={!isUndefined(endTimestamp)}
            comment={comments[0]}
            secondaryActions={
              <div>
                {comments.length === 1 ? (
                  canReplyToComment && !isAddingReply && <ReplyButton discussionId={discussion.id} />
                ) : (
                  <Button
                    appearance="ghost"
                    color="gray"
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleReplies();
                    }}
                    size="small"
                    prefix={areRepliesVisible ? <ChevronDownIcon size="xs" /> : <ChevronRightIcon size="xs" />}
                  >
                    {pluralize('Reply', comments.length - 1, true)}{' '}
                  </Button>
                )}
              </div>
            }
            {...commentProps}
          />
        </TopCommentContainer>

        {(areRepliesVisible || isAddingReply) && (
          <div className="border-t ads:border-t-ads-gray-5 border-t-gray-5 ads:bg-raised bg-gray-2">
            {areRepliesVisible && (
              <div>
                {comments.slice(1).map((comment, index) => (
                  <DiscussionComment
                    key={comment.id}
                    comment={comment}
                    secondaryActions={
                      canReplyToComment &&
                      !isAddingReply &&
                      index === comments.length - 2 && <ReplyButton discussionId={discussion.id} />
                    }
                    style={{ paddingLeft: COMMENT_AUTHOR_HEADER_LEFT }}
                    {...commentProps}
                  />
                ))}
              </div>
            )}
            {isAddingReply && (
              <CommentReply
                user={data}
                discussionId={discussion.id}
                shouldShowMentionsButton={shouldShowMentionsButton}
                isLoadingMentions={isLoadingMentions}
                mentionsList={mentionsList}
                onSubmit={onReplySubmit}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);

ClipDiscussion.displayName = 'ClipDiscussion';
